// src/templates/requests/RequestsManager.jsx
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { requestsService } from '../../services/requestsService';

import ProviderAdmin from './ProviderAdmin';
import ProviderSettings from './ProviderSettings';
import ProviderAnalytics from './ProviderAnalytics';

const Tabs = {
  REQUESTS: 'requests',
  OFFERS: 'offers',
  PROVIDERS: 'providers',
  PROVIDER_SETTINGS: 'provider-settings',
  ANALYTICS: 'analytics',
};

const RequestsManager = ({ bookId, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState(Tabs.REQUESTS);

  const [requests, setRequests] = useState([]);
  const [offers, setOffers] = useState([]);
  const [providers, setProviders] = useState([]);

  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);

  // -------------------------------------------------------------------
  // Load all data
  // -------------------------------------------------------------------
  useEffect(() => {
    if (isOpen) loadAll();
  }, [isOpen]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const baseStats = await requestsService.getStats(bookId);
      setStats(baseStats);

      // Load requests
      const allRequests = await requestsService.getUserRequests(bookId, null);
      setRequests(allRequests);

      // Load offers (admin view)
      const allOffers = await requestsService.getAllOffers?.(bookId) ?? [];
      setOffers(allOffers);

      // Load provider profiles
      const providerList = await requestsService.getProviderProfiles?.(bookId) ?? [];
      setProviders(providerList);
    } catch (error) {
      console.error('Failed to load Requests Manager:', error);
    } finally {
      setLoading(false);
    }
  };

  // -------------------------------------------------------------------
  // Update request & offer statuses
  // -------------------------------------------------------------------
  const updateRequestStatus = async (requestId, status) => {
    await requestsService.updateRequestStatus(bookId, requestId, status);
    setRequests(prev =>
      prev.map(r => (r.id === requestId ? { ...r, status } : r))
    );
  };

  const updateOfferStatus = async (offerId, status) => {
    await requestsService.updateOfferStatus(bookId, offerId, status);
    setOffers(prev =>
      prev.map(o => (o.id === offerId ? { ...o, status } : o))
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-6xl h-[90vh] flex flex-col overflow-hidden">

        {/* --------------------------------------------------------------
            Header 
        -------------------------------------------------------------- */}
        <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">Requests Manager</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* --------------------------------------------------------------
            Tabs
        -------------------------------------------------------------- */}
        <div className="border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800">
          <div className="flex gap-4 px-6">

            {Object.values(Tabs).map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition ${
                  activeTab === tab
                    ? 'border-indigo-600 text-indigo-600'
                    : 'border-transparent text-slate-600 dark:text-slate-300 hover:text-slate-900'
                }`}
              >
                {tab === Tabs.REQUESTS && "Requests"}
                {tab === Tabs.OFFERS && "Offers"}
                {tab === Tabs.PROVIDERS && "Providers"}
                {tab === Tabs.PROVIDER_SETTINGS && "Provider Settings"}
                {tab === Tabs.ANALYTICS && "Analytics"}
              </button>
            ))}

          </div>
        </div>

        {/* --------------------------------------------------------------
            Content
        -------------------------------------------------------------- */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-20 text-slate-500">Loading data…</div>
          ) : (
            <>
              {activeTab === Tabs.REQUESTS && (
                <RequestsTab
                  requests={requests}
                  onStatusChange={updateRequestStatus}
                />
              )}

              {activeTab === Tabs.OFFERS && (
                <OffersTab
                  offers={offers}
                  onStatusChange={updateOfferStatus}
                />
              )}

              {activeTab === Tabs.PROVIDERS && (
                <ProviderAdmin bookId={bookId} />
              )}

              {activeTab === Tabs.PROVIDER_SETTINGS && (
                <ProviderSettings bookId={bookId} />
              )}

              {activeTab === Tabs.ANALYTICS && (
                <ProviderAnalytics bookId={bookId} />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequestsManager;

/* =========================================================================
   SUBCOMPONENTS (Requests / Offers / Providers / Stats)
   ========================================================================= */

const RequestsTab = ({ requests, onStatusChange }) => {
  if (!requests.length) return <Empty text="No requests submitted yet." />;

  return (
    <div className="space-y-4">
      {requests.map(req => (
        <div
          key={req.id}
          className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700"
        >
          <div className="flex justify-between">
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">
                {req.description}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Timeframe: {req.timeframe}
              </p>
              <p className="text-xs text-slate-500">
                Location: {req.location || 'N/A'}
              </p>
            </div>

            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={req.status} />

              <select
                className="px-3 py-1 rounded border text-sm bg-white dark:bg-slate-900"
                value={req.status}
                onChange={e => onStatusChange(req.id, e.target.value)}
              >
                {['submitted', 'in-progress', 'accepted', 'closed'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const OffersTab = ({ offers, onStatusChange }) => {
  if (!offers.length) return <Empty text="No offers have been submitted." />;

  return (
    <div className="space-y-4">
      {offers.map(offer => (
        <div
          key={offer.id}
          className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700"
        >
          <div className="flex justify-between">
            <div>
              <p className="font-semibold text-slate-900 dark:text-white">
                {offer.offerTitle}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-300">
                {offer.description}
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Provider: {offer.providerName}
              </p>
            </div>

            <div className="flex flex-col items-end gap-2">
              <StatusBadge status={offer.status} />

              <select
                className="px-3 py-1 rounded border text-sm bg-white dark:bg-slate-900"
                value={offer.status}
                onChange={(e) => onStatusChange(offer.id, e.target.value)}
              >
                {['PENDING', 'ACCEPTED', 'REJECTED'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const ProvidersTab = ({ providers }) => {
  if (!providers.length) return <Empty text="No provider profiles exist yet." />;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {providers.map(p => (
        <div
          key={p.id}
          className="p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700"
        >
          <p className="font-semibold text-slate-900 dark:text-white">
            {p.businessName}
          </p>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Category: {p.category}
          </p>
          <p className="text-xs mt-2 text-slate-500">
            Provider ID: {p.providerUserId}
          </p>
        </div>
      ))}
    </div>
  );
};

const Empty = ({ text }) => (
  <div className="text-center text-slate-500 py-20">{text}</div>
);

const StatusBadge = ({ status }) => {
  const colours = {
    submitted: "bg-blue-100 text-blue-700",
    "in-progress": "bg-amber-100 text-amber-700",
    accepted: "bg-emerald-100 text-emerald-700",
    closed: "bg-slate-200 text-slate-700",
    PENDING: "bg-slate-200 text-slate-700",
    ACCEPTED: "bg-emerald-100 text-emerald-700",
    REJECTED: "bg-red-100 text-red-700",
  };

  return (
    <span
      className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
        colours[status] || "bg-slate-200 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
};
